import type { Dispatch, SetStateAction } from 'react'
import {
  Badge,
  Button,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../ui'
import type { ScheduleGrid } from '../../lib/klassenbuchApi'

type MetaItem = { id: number; name: string; code?: string; start_date?: string; end_date?: string }

type Candidate = { id: number; label: string }

export type BlockingQuestion = {
  id: string
  type: string
  message: string
  untis_ref: string
  untis_refs?: string[]
  candidates?: Candidate[]
  llm_suggestion?: {
    source?: string
    target_id?: number
    confidence?: number | null
    reason?: string | null
  }
}

export type ImportSummaryTeacher = { teacher_code: string; teacher_display_name: string; entry_count: number }

export type LdapTeacherPending = { teacher_code: string; ldap_cn: string; display_name: string }

export type LdapCommitTeachers = {
  users_created: Array<{ teacher_code: string; user_id: number; ldap_cn: string }>
  additional_untis_codes_mapped: Array<{ teacher_code: string; user_id: number; ldap_cn: string }>
}

export type CreatedCourse = {
  id: number
  untis_course_key: string
  name: string
  fach?: string | null
  jahrgang?: string | null
}

export type TimetableResolution = { action: string; target_id?: number }

type FileFieldLabel = { key: string; label: string }

interface TimetableUntisImportPanelProps {
  schoolYears: MetaItem[]
  selectedSchoolYearId: string
  setSelectedSchoolYearId: (value: string) => void
  locations: MetaItem[]
  selectedLocationId: string
  setSelectedLocationId: (value: string) => void
  label: string
  setLabel: (value: string) => void
  scheduleGrids: ScheduleGrid[]
  defaultScheduleGridId: string
  selectedScheduleGridId: string
  setSelectedScheduleGridId: (value: string) => void
  fileFieldLabels: FileFieldLabel[]
  handleFileChange: (key: string, file: File | null) => void
  canDryRun: boolean
  untisImportBusy: boolean
  sessionUid: string
  unresolvedCount: number
  error: string | null
  successMessage: string | null
  blockingQuestions: BlockingQuestion[]
  resolutions: Record<string, TimetableResolution>
  setResolutions: Dispatch<SetStateAction<Record<string, TimetableResolution>>>
  unresolvedCourseNotFoundCount: number
  llmTeacherSuggestionCount: number
  applyCreateCourseResolutions: () => void
  applyLlmTeacherSuggestions: () => void
  updateResolution: (questionId: string, action: string, targetId?: number) => void
  resolutionActionForQuestionType: (type: string) => string
  teachersWithoutAccount: ImportSummaryTeacher[]
  ldapDryRunHints: { linkedCodes: number; pendingCreate: LdapTeacherPending[] }
  ldapCommitSummary: LdapCommitTeachers | null
  createdCourses: CreatedCourse[]
  onDryRun: () => void
  onImportDraft: () => void
  onImportAndActivate: () => void
}

export default function TimetableUntisImportPanel({
  schoolYears,
  selectedSchoolYearId,
  setSelectedSchoolYearId,
  locations,
  selectedLocationId,
  setSelectedLocationId,
  label,
  setLabel,
  scheduleGrids,
  defaultScheduleGridId,
  selectedScheduleGridId,
  setSelectedScheduleGridId,
  fileFieldLabels,
  handleFileChange,
  canDryRun,
  untisImportBusy,
  sessionUid,
  unresolvedCount,
  error,
  successMessage,
  blockingQuestions,
  resolutions,
  setResolutions,
  unresolvedCourseNotFoundCount,
  llmTeacherSuggestionCount,
  applyCreateCourseResolutions,
  applyLlmTeacherSuggestions,
  updateResolution,
  resolutionActionForQuestionType,
  teachersWithoutAccount,
  ldapDryRunHints,
  ldapCommitSummary,
  createdCourses,
  onDryRun,
  onImportDraft,
  onImportAndActivate
}: TimetableUntisImportPanelProps) {
  return (
    <div className="space-y-6">
      <section className="card p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Schuljahr
            <select
              className="input-field mt-1"
              value={selectedSchoolYearId}
              onChange={(event) => setSelectedSchoolYearId(event.target.value)}
            >
              <option value="">Bitte wählen</option>
              {schoolYears.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-700 dark:text-gray-300">
            Standort (optional)
            <select
              className="input-field mt-1"
              value={selectedLocationId}
              onChange={(event) => setSelectedLocationId(event.target.value)}
            >
              <option value="">Schulweit (kein Standort)</option>
              {locations.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-700 dark:text-gray-300">
            Bezeichnung
            <input
              className="input-field mt-1"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="z. B. UNTIS Import 2026-05"
            />
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Zeitraster
            <select
              className="input-field mt-1"
              value={selectedScheduleGridId}
              onChange={(event) => setSelectedScheduleGridId(event.target.value)}
            >
              <option value="">Bitte wählen</option>
              {scheduleGrids.map((grid) => (
                <option key={grid.id} value={grid.id}>
                  {grid.name}
                  {grid.id === defaultScheduleGridId ? ' (Standard)' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {fileFieldLabels.map((field) => (
            <label key={field.key} className="text-sm text-gray-700 dark:text-gray-300">
              {field.label}
              <input
                type="file"
                accept=".txt,text/plain"
                className="input-field mt-1"
                onChange={(event) => handleFileChange(field.key, event.target.files?.[0] || null)}
              />
            </label>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onDryRun} disabled={untisImportBusy || !canDryRun}>
            Dry-Run starten
          </Button>
          <Button variant="outline" onClick={onImportDraft} disabled={untisImportBusy || !sessionUid || unresolvedCount > 0}>
            Als Entwurf importieren
          </Button>
          <Button variant="primary" onClick={onImportAndActivate} disabled={untisImportBusy || !sessionUid || unresolvedCount > 0}>
            Importieren & aktivieren
          </Button>
          {sessionUid && <Badge variant="info">Session: {sessionUid.slice(0, 8)}…</Badge>}
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {successMessage && <p className="text-sm text-green-600 dark:text-green-400">{successMessage}</p>}
      </section>

      <section className="card p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Offene Zuordnungen (Blocker)</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={applyCreateCourseResolutions}
              disabled={unresolvedCourseNotFoundCount === 0}
            >
              Alle fehlenden Kurse anlegen ({unresolvedCourseNotFoundCount})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={applyLlmTeacherSuggestions}
              disabled={llmTeacherSuggestionCount === 0}
            >
              HUB-Vorschläge übernehmen ({llmTeacherSuggestionCount})
            </Button>
          </div>
        </div>
        {blockingQuestions.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Keine blockierenden Konflikte.</p>
        ) : (
          <div className="space-y-3">
            {blockingQuestions.map((question) => (
              <div key={question.id} className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="warning">{question.type}</Badge>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    UNTIS-ID
                    {question.untis_refs && question.untis_refs.length > 1
                      ? ` (${question.untis_refs.length}): ${question.untis_refs.slice(0, 8).join(', ')}${
                          question.untis_refs.length > 8 ? '…' : ''
                        }`
                      : `: ${question.untis_ref}`}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-800 dark:text-gray-200">{question.message}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {question.candidates && question.candidates.length > 0 ? (
                    <>
                      <select
                        className="input-field min-w-[18rem]"
                        value={String(resolutions[question.id]?.target_id || '')}
                        onChange={(event) => {
                          const selected = Number(event.target.value)
                          if (!selected) {
                            setResolutions((prev) => {
                              const next = { ...prev }
                              delete next[question.id]
                              return next
                            })
                            return
                          }
                          updateResolution(question.id, resolutionActionForQuestionType(question.type), selected)
                        }}
                      >
                        <option value="">Bitte wählen</option>
                        {question.candidates.map((candidate) => (
                          <option key={candidate.id} value={candidate.id}>
                            {candidate.label}
                          </option>
                        ))}
                      </select>
                      {question.type === 'teacher_ambiguous' && (
                        <Button size="sm" variant="outline" onClick={() => updateResolution(question.id, 'keep_unlinked')}>
                          Unverknüpft lassen
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {question.type === 'course_not_found' && (
                        <Button size="sm" variant="outline" onClick={() => updateResolution(question.id, 'create_course')}>
                          Kurs anlegen
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => updateResolution(question.id, 'skip')}>
                        Zeile überspringen
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card p-4 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Lehrkräfte ohne LR-Account</h2>
        {(ldapDryRunHints.linkedCodes > 0 || ldapDryRunHints.pendingCreate.length > 0) && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200">
            {ldapDryRunHints.linkedCodes > 0 && (
              <p>
                LDAP: {ldapDryRunHints.linkedCodes} UNTIS-Kürzel wurden mit bestehenden LR-Konten (Benutzername = LDAP-cn)
                verknüpft.
              </p>
            )}
            {ldapDryRunHints.pendingCreate.length > 0 && (
              <p className={ldapDryRunHints.linkedCodes > 0 ? 'mt-2' : ''}>
                Beim Import werden voraussichtlich {ldapDryRunHints.pendingCreate.length} Lehrkraft-Konten aus LDAP
                angelegt (eindeutiger Treffer in OU Teachers, noch kein lokales Konto).
              </p>
            )}
          </div>
        )}
        {teachersWithoutAccount.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Keine offenen Lehrkraft-Zuordnungen.</p>
        ) : (
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Kürzel</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell align="right">Einträge</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teachersWithoutAccount.map((teacher) => (
                  <TableRow key={`${teacher.teacher_code}-${teacher.teacher_display_name}`}>
                    <TableCell>{teacher.teacher_code}</TableCell>
                    <TableCell>{teacher.teacher_display_name}</TableCell>
                    <TableCell align="right">{teacher.entry_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScrollArea>
        )}
      </section>

      <section className="card p-4 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Neu angelegte Kurse</h2>
        {ldapCommitSummary &&
          (ldapCommitSummary.users_created.length > 0 || ldapCommitSummary.additional_untis_codes_mapped.length > 0) && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200">
              {ldapCommitSummary.users_created.length > 0 && (
                <p>LDAP: {ldapCommitSummary.users_created.length} neue Lehrkraft-Konten aus dem Teachers-OU angelegt.</p>
              )}
              {ldapCommitSummary.additional_untis_codes_mapped.length > 0 && (
                <p className={ldapCommitSummary.users_created.length > 0 ? 'mt-2' : ''}>
                  LDAP: {ldapCommitSummary.additional_untis_codes_mapped.length} weitere UNTIS-Kürzel derselben Person
                  zugeordnet (external_teacher_map).
                </p>
              )}
            </div>
          )}
        {createdCourses.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Im letzten Commit wurden keine neuen Kurse angelegt.</p>
        ) : (
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Kurs</TableHeaderCell>
                  <TableHeaderCell>Fach</TableHeaderCell>
                  <TableHeaderCell>Jahrgang</TableHeaderCell>
                  <TableHeaderCell>UNTIS-Key</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {createdCourses.map((course) => (
                  <TableRow key={`${course.id}-${course.untis_course_key}`}>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>{course.fach || '—'}</TableCell>
                    <TableCell>{course.jahrgang || '—'}</TableCell>
                    <TableCell>{course.untis_course_key}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScrollArea>
        )}
      </section>
    </div>
  )
}
